<template>
	<div
		v-if="isLoading"
		class="fixed inset-0 z-[60] flex items-center justify-center bg-black/20 backdrop-blur-sm"
	>
		<LoaderDots :isLoading="isLoading" />
	</div>
	<BaseDialog
		:isOpen="isOpen"
		@close="close"
		@runAfterLeave="runAfterLeave"
		:isSolutionModal="true"
	>
		<template #default>
			<Transition
				enter-active-class="transition-opacity duration-400 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition-opacity duration-300 ease-in"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<div
					v-if="isOpen"
					class="fixed inset-0 flex items-center justify-center z-50 bg-black/20 backdrop-blur-sm antialiased md:p-4"
					@click="handleBackdropClick"
				>
					<Transition
						enter-active-class="transition-all duration-500 ease-out"
						enter-from-class="opacity-0 translate-y-4"
						enter-to-class="opacity-100 translate-y-0"
						leave-active-class="transition-all duration-300 ease-in"
						leave-from-class="opacity-100 translate-y-0"
						leave-to-class="opacity-0 translate-y-4"
						appear
					>
						<div
							v-if="isOpen"
							class="solution-modal relative flex h-screen-dynamic w-screen-dynamic flex-col overflow-hidden pb-8 md:h-[80vh] h-[98vh] md:w-[85vw] w-[98vw] bg-white rounded-xl shadow-2xl border border-slate-200/50 transform-gpu"
							id="hotspot-body"
							@click.stop
						>
							<div
								v-if="isNavigating"
								class="absolute left-0 top-0 z-[100] h-[2px] w-full overflow-hidden rounded-t-xl bg-slate-100"
							>
								<div class="h-full w-1/3 animate-pulse rounded-full bg-blue-500" />
							</div>
							<Transition
								enter-active-class="transition-all duration-600 ease-out delay-150"
								enter-from-class="opacity-0 translate-y-3"
								enter-to-class="opacity-100 translate-y-0"
								appear
							>
								<header
									class="sticky top-0 z-999 flex items-center justify-between rounded-t-xl px-4 py-4 text-xl md:px-6 border-b border-gray-200 bg-white"
								>
									<div class="flex items-center gap-2">
										<template v-if="isProcessEquipmentLayer">
											<button
												@click="prevProcessEquipment"
												class="p-2 hover:bg-slate-50 rounded-xl transition-all duration-200 group hover:scale-105 active:scale-95 cursor-pointer hidden md:block"
												title="Previous step"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													stroke-width="2"
													stroke-linecap="round"
													stroke-linejoin="round"
													class="w-6 h-6 text-slate-500 group-hover:text-slate-700 transition-all duration-200"
												>
													<path d="m12 19-7-7 7-7" />
													<path d="M19 12H5" />
												</svg>
											</button>
											<button
												@click="nextProcessEquipment"
												class="p-2 hover:bg-slate-50 rounded-xl transition-all duration-200 group hover:scale-105 active:scale-95 cursor-pointer hidden md:block"
												title="Next step"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="24"
													height="24"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													stroke-width="2"
													stroke-linecap="round"
													stroke-linejoin="round"
													class="w-6 h-6 text-slate-500 group-hover:text-slate-700 transition-all duration-200"
												>
													<path d="M12 5l7 7-7 7" />
													<path d="M5 12h14" />
												</svg>
											</button>
										</template>
										<button
											v-else
											@click="goBack"
											class="p-2 hover:bg-slate-50 rounded-xl transition-all duration-200 group hover:scale-105 active:scale-95 cursor-pointer hidden md:block"
											title="Go back"
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="24"
												height="24"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
												class="w-6 h-6 text-slate-500 group-hover:text-slate-700 transition-all duration-200"
											>
												<path d="m12 19-7-7 7-7" />
												<path d="M19 12H5" />
											</svg>
										</button>
										<img
											v-if="hotspot.portfolio == 7 || hotspot.portfolio == 8"
											:src="
												hotspot.portfolio == 7
													? '/images/icons/innovation-icon.svg'
													: '/images/icons/water-saving.svg'
											"
											alt="value driver icon"
											class="w-5.5 h-5.5 transition-transform duration-200 hover:scale-110"
										/>

										<h1
											class="text-sm sm:text-lg font-bold text-slate-950 md:block md:text-xl"
											v-html="hotspot.title"
										></h1>
									</div>

									<div class="flex items-center gap-2">
										<button
											@click="close"
											class="p-2 hover:bg-slate-50 rounded-xl transition-all duration-200 group hover:scale-105 active:scale-95 cursor-pointer"
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="24"
												height="24"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
												class="w-6 h-6 text-slate-500 group-hover:text-slate-700 transition-all duration-200"
											>
												<line x1="18" y1="6" x2="6" y2="18"></line>
												<line x1="6" y1="6" x2="18" y2="18"></line>
											</svg>
										</button>
									</div>
								</header>
							</Transition>

							<main class="flex h-full w-full flex-col md:flex-row @container">
								<aside
									v-if="hasImages"
									class="hidden md:block relative top-4 z-50 mr-5 h-[40vh] md:sticky md:top-9 md:ml-5 md:mt-9 md:w-[38%] aspect-[553/311]"
								>
									<!-- Desktop / Tablet Gallery -->
									<ImageGallery
										class="z-999 hidden md:block shadow-lg"
										:images="images"
										minimizedHeight="80%"
										minimizedWidth="calc(100%)"
										maximizedHeight="63vh"
									/>
								</aside>

								<Transition
									enter-active-class="transition-all duration-700 ease-out delay-250"
									enter-from-class="opacity-0 translate-y-4"
									enter-to-class="opacity-100 translate-y-0"
									appear
								>
									<section class="flex-1 h-full overflow-y-auto solution pb-8">
										<div v-if="hasImages" class="md:hidden px-4 pt-6">
											<ImageGallery
												:images="images"
												minimizedHeight="240px"
												minimizedWidth="100%"
												:canFullScreen="false"
											/>
										</div>

										<div class="p-6 space-y-8">
											<Transition
												enter-active-class="transition-all duration-600 ease-out delay-350"
												enter-from-class="opacity-0 translate-y-4"
												enter-to-class="opacity-100 translate-y-0"
												appear
											>
												<section v-if="isOpen" class="space-y-6">
													<h3
														v-if="hotspot.headline"
														v-html="hotspot.headline"
														class="mb-7 text-xl text-slate-950 md:text-2xl font-bold"
													></h3>
													<div
														v-if="hotspot.description"
														v-html="hotspot.description"
														class="**:!text-slate-950/70 **:!text-sm **:!leading-6 prose prose-slate max-w-none"
													></div>

													<MoreButton
														v-if="hotspot.more_info"
														:url="hotspot.more_info"
														:portfolio="hotspot.portfolio"
													/>
												</section>
											</Transition>

											<div
												class="*:border-t *:border-neutral-200 *:pt-8 *:pb-9 space-y-0"
												:class="
													!hotspot.headline &&
													!hotspot.description &&
													!hotspot.more_info
														? '*:first:border-t-0 *:first:pt-0'
														: ''
												"
											>
												<!-- Related Solutions Section -->
												<Transition
													enter-active-class="transition-all duration-600 ease-out delay-450"
													enter-from-class="opacity-0 translate-y-3"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<section
														v-if="
															hotspot &&
															relatedSolutions &&
															relatedSolutions.length > 0 &&
															isOpen
														"
														class="space-y-6"
													>
														<h3
															class="text-lg font-bold text-slate-950 capitalize"
														>
															{{ getTranslations("related solutions") }}
														</h3>
														<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
															<RouterLink
																v-for="related in relatedSolutions"
																:key="related.link"
																:to="related.link"
																class="group p-4 rounded-xl border border-slate-200 transition-all duration-300"
																:class="
																	hotspot.portfolio == 7
																		? 'hover:border-orange-500 hover:bg-orange-50/50'
																		: hotspot.portfolio == 8
																			? 'hover:border-teal-500 hover:bg-teal-50/50'
																			: 'hover:border-blue-300 hover:bg-blue-50/50'
																"
															>
																<div class="flex items-center justify-between">
																	<p
																		class="text-sm font-semibold text-slate-700 transition-colors duration-200"
																		:class="
																			hotspot.portfolio == 7
																				? 'group-hover:text-orange-500'
																				: hotspot.portfolio == 8
																					? 'group-hover:text-teal-500'
																					: 'group-hover:text-blue-700 '
																		"
																	>
																		{{ related.title }}
																	</p>
																	<svg
																		class="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200"
																		fill="none"
																		stroke="currentColor"
																		viewBox="0 0 24 24"
																		:class="
																			hotspot.portfolio == 7
																				? 'text-orange-500'
																				: hotspot.portfolio == 8
																					? 'text-teal-500'
																					: 'text-blue-500'
																		"
																	>
																		<path
																			stroke-linecap="round"
																			stroke-linejoin="round"
																			stroke-width="2"
																			d="M9 5l7 7-7 7"
																		/>
																	</svg>
																</div>
															</RouterLink>
														</div>
													</section>
												</Transition>

												<!-- Value Drivers Section -->
												<Transition
													enter-active-class="transition-all duration-600 ease-out delay-550"
													enter-from-class="opacity-0 translate-y-3"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<section
														v-if="currentValueDrivers.length > 0 && isOpen"
														class="space-y-6"
													>
														<h3
															class="text-lg font-bold text-slate-950 capitalize"
														>
															{{ getTranslations("value drivers") }}
														</h3>
														<div
															class="grid grid-cols-1 sm:grid-cols-2 gap-4 **:cursor-default"
														>
															<div
																v-for="driver in valueDrivers"
																:key="driver.id"
																class="flex items-center gap-3 p-3 rounded-xl bg-slate-50/80 transition-all duration-200"
															>
																<div
																	class="p-2 bg-white rounded-lg shadow-sm transition-transform duration-200"
																>
																	<img
																		width="20"
																		height="20"
																		:alt="driver.name"
																		:src="`/images/value-drivers/${driver.id}.svg`"
																		class="w-6 h-6 object-contain"
																	/>
																</div>
																<p class="text-sm font-semibold text-slate-700">
																	{{ driver.name }}
																</p>
															</div>
														</div>
													</section>
												</Transition>

												<!-- Highlighted Solutions Section -->
												<Transition
													enter-active-class="transition-all duration-600 ease-out delay-650"
													enter-from-class="opacity-0 translate-y-3"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<section
														v-if="highlightedSolutions.length > 0 && isOpen"
														class="space-y-6"
													>
														<h3
															class="text-lg font-bold text-slate-950 capitalize"
														>
															{{ getTranslations("highlighted solutions") }}
														</h3>
														<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
															<RouterLink
																v-for="(
																	solution, index
																) in highlightedSolutions"
																:key="index"
																:to="solution.link"
																class="group p-4 rounded-xl border border-slate-200 transition-all duration-300"
																:class="
																	hotspot.portfolio == 7
																		? 'hover:border-orange-500 hover:bg-orange-50/50'
																		: hotspot.portfolio == 8
																			? 'hover:border-teal-500 hover:bg-teal-50/50'
																			: 'hover:border-blue-300 hover:bg-blue-50/50'
																"
															>
																<div class="flex items-center justify-between">
																	<p
																		class="text-sm font-semibold text-slate-700 transition-colors duration-200"
																		:class="
																			hotspot.portfolio == 7
																				? 'group-hover:text-orange-500'
																				: hotspot.portfolio == 8
																					? 'group-hover:text-teal-500'
																					: 'group-hover:text-blue-700 '
																		"
																	>
																		{{ solution.title }}
																	</p>
																	<svg
																		class="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200"
																		fill="none"
																		stroke="currentColor"
																		viewBox="0 0 24 24"
																		:class="
																			hotspot.portfolio == 7
																				? 'text-orange-500'
																				: hotspot.portfolio == 8
																					? 'text-teal-500'
																					: 'text-blue-500'
																		"
																	>
																		<path
																			stroke-linecap="round"
																			stroke-linejoin="round"
																			stroke-width="2"
																			d="M9 5l7 7-7 7"
																		/>
																	</svg>
																</div>
															</RouterLink>
														</div>
													</section>
												</Transition>

												<!-- Related Content Section -->
												<Transition
													enter-active-class="transition-all duration-500 ease-out delay-800"
													enter-from-class="opacity-0 translate-y-[20px]"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<section
														v-if="relatedContent.length > 0 && isOpen"
														class="space-y-6"
													>
														<h3
															class="text-lg font-bold text-slate-950 capitalize"
														>
															{{ getTranslations("related content") }}
														</h3>
														<div
															class="flex w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4"
														>
															<a
																:href="related.url"
																target="_blank"
																v-for="(related, index) in relatedContent"
																:key="index"
																class="h-28"
															>
																<SquareArrowButton
																	:title="related.title"
																	type="content"
																	:contentType="related.type"
																	:contentTypeId="related.id"
																/>
															</a>
														</div>
													</section>
												</Transition>

												<!-- Videos Section -->
												<Transition
													enter-active-class="transition-all duration-500 ease-out delay-900"
													enter-from-class="opacity-0 translate-y-[20px]"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<section
														v-if="hotspot.videos.length > 0 && isOpen"
														class="space-y-6"
													>
														<h3
															class="text-lg font-bold text-slate-950 capitalize"
														>
															{{ getTranslations("videos") }}
														</h3>
														<div
															class="flex w-full flex-wrap items-center gap-4"
															v-for="(video, index) in hotspot.videos"
															:key="index"
														>
															<iframe
																class="aspect-video w-full"
																:src="video"
																allowfullscreen
															></iframe>
														</div>
													</section>
												</Transition>

												<!-- Share Button Section -->
												<Transition
													enter-active-class="transition-all duration-500 ease-out delay-1000"
													enter-from-class="opacity-0 translate-y-[20px]"
													enter-to-class="opacity-100 translate-y-0"
													appear
												>
													<section v-if="isOpen" class="space-y-6">
														<div class="flex justify-start">
															<button
																@click="copyLinkToClipboard"
																ref="copyButton"
																class="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors duration-200 cursor-pointer group"
																title="Share this content"
															>
																<svg
																	xmlns="http://www.w3.org/2000/svg"
																	width="16"
																	height="16"
																	viewBox="0 0 24 24"
																	fill="none"
																	stroke="currentColor"
																	stroke-width="2"
																	stroke-linecap="round"
																	stroke-linejoin="round"
																	class="w-4 h-4 transition-colors duration-200"
																>
																	<circle cx="18" cy="5" r="3"></circle>
																	<circle cx="6" cy="12" r="3"></circle>
																	<circle cx="18" cy="19" r="3"></circle>
																	<line
																		x1="8.59"
																		y1="13.51"
																		x2="15.42"
																		y2="17.49"
																	></line>
																	<line
																		x1="15.41"
																		y1="6.51"
																		x2="8.59"
																		y2="10.49"
																	></line>
																</svg>
																<span class="text-sm capitalize no-underline">{{
																	getTranslations("share")
																}}</span>
															</button>
														</div>
													</section>
												</Transition>
											</div>
										</div>
									</section>
								</Transition>
							</main>
						</div>
					</Transition>
				</div>
			</Transition>
		</template>
	</BaseDialog>
</template>
<script src="./HotspotModal"></script>
<style scoped lang="scss" src="./HotspotModal.scss"></style>
